import React from 'react'
import { Button, Col, Collapse, Input, Select, Typography, Space, Popconfirm, message } from 'antd'
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons'
import * as deepObject from 'deep-object-diff'
import { FormComponent } from '../../../BaseComponent'
import { ShelterKioskReasonContextData } from '../../../../context/ShelterKioskReasonContext'
import { LanguageConsumer } from '../../../../context/LanguageContext'
import ILanguage from '../../../../interfaces/ILanguage'
import ITranslationShelterKioskReason from '../../../../interfaces/ITranslationShelterKioskReason'

type ShelterReasonTranslationProps = {
  branchUuid: string,
  reasonUuid: string,

  context: ShelterKioskReasonContextData
}

export default class ShelterReasonTranslation extends FormComponent<ShelterReasonTranslationProps> {

  state = {
    translations: [] as ITranslationShelterKioskReason[]
  }

  componentDidMount() {
    this.setState({
      translations: this.getReasonTranslation()
    })
  }

  componentDidUpdate(prevProps: ShelterReasonTranslationProps) {
    const hasChanged = Object.keys(deepObject.diff(this.props.context.shelterKioskReasons || [], prevProps.context.shelterKioskReasons || [])).length > 0
    if (hasChanged) {
      this.setState({
        translations: this.getReasonTranslation()
      })
    }
  }

  private getReasonTranslation = () => {
    const { branchUuid, reasonUuid, context } = this.props
    
    const translations: ITranslationShelterKioskReason[] = []
    const reasons = context.shelterKioskReasons?.filter(v => v.branch?.uuid === branchUuid && v.reason?.uuid === reasonUuid) || []
    
    reasons.forEach(v => v.translation && translations.push(...v.translation))

    return translations
  }

  private getReason = () => {
    const { branchUuid, reasonUuid, context } = this.props

    return context.shelterKioskReasons?.find(v => v.branch?.uuid === branchUuid && v.reason?.uuid === reasonUuid)
  }

  private handleAdd = () => {
    const { translations } = this.state
    const reason = this.getReason()

    const fakeUuid = `fake-uuid-${translations.length}`
    translations.push({
      parent_id: reason?.id || 0,
      language_id: 0,
      name: '',
      end_message: '',
      uuid: fakeUuid
    })

    this.setState({
      translations
    })
  }

  private handleRemove = (uuid: string) => {
    const { context } = this.props
    const { translations } = this.state
    
    this.setState({
      translations: translations.filter(v => v.uuid !== uuid)
    }, async () => {
      if (!uuid.includes('fake')) {
        await context.requestShelterKioskReasonTranslationDelete({
          shelter_kiosk_reason_translation_uuid: uuid
        })
      }
    })
  }

  private handleSave = async (values: ITranslationShelterKioskReason) => {
    const { context } = this.props

    try {
      if (values.uuid.includes('fake')) {
        (values as any).uuid = null
      }

      await context.requestShelterKioskReasonTranslationSave({
        shelter_kiosk_reason_translation: values
      })
      message.success('Shelter kiosk reason traslation saved successfully!')
    } catch (error) {
      message.error(`Failed to save shelter kiosk reason translation. Reason: ${error}`)
    }
  }

  private renderTranslation = (languages: ILanguage[]) => {
    const { context } = this.props
    const { translations } = this.state

    return translations.map((v, index) => {
      if (!v) return null

      const value = this.getFieldValue(`translations[${index}].language_id`)
      const language = languages.find(v => v.id === value)
      const canSave = v.language_id > 0

      return (
        <Collapse.Panel
          header={language?.name || 'Language not selected'}
          key={`translation-${index}`}
          extra={(
            <Space direction="horizontal">
              <Popconfirm
                title="Are you sure about deleting this translation?"
                onConfirm={() => this.handleRemove(v.uuid)}
              >
                <Button
                  type="dashed"
                  shape="circle"
                  size="small"
                >
                  <DeleteOutlined />
                </Button>
              </Popconfirm>

              <Button
                type="primary"
                shape="round"
                size="small"
                disabled={!canSave}
                loading={context.fetching || false}
                onClick={() => this.handleSave(v)}
              >
                Save
              </Button>
            </Space>
          )}
        >
          <Col>
            <Col xs={16}>
              <Select
                placeholder="Language"
                style={{
                  width: '85%'
                }}
                options={languages.filter(p => p.id !== undefined).map(p => {
                  return {
                    label: p.name,
                    value: p.id
                  }
                }) as any}
                value={value > 0 ? value : undefined}
                onChange={(value) => this.onFieldsChange({target: { name: `translations[${index}].language_id`, value }})}
              />
            </Col>

            <Col xs={8} style={{marginTop:4}}>
              {
                value > 0 ? (
                  <Input
                    placeholder="Reason name"
                    name={`translations[${index}].name`}
                    value={this.getFieldValue(`translations[${index}].name`)}
                    onChange={(ev: any) => this.onFieldsChange(ev)}
                    style={{
                      width: '100%'
                    }}
                  />
                ) : null
              }
            </Col>
          </Col>

          {
            value > 0 ? (
              <Input.TextArea
                placeholder="End message (displayed when user finishes checking in)"
                name={`translations[${index}].end_message`}
                value={this.getFieldValue(`translations[${index}].end_message`)}
                onChange={(ev: any) => this.onFieldsChange(ev)}
                style={{
                  marginTop: 10
                }}
              />
            ) : null
          }
        </Collapse.Panel>
      )
    })
  }

  render() {
    return (
      <LanguageConsumer>
        {({ languages }) => {
          if (!languages) return null

          return (
            <>
              <Typography.Title level={5}>
                Reason translations
                <Button
                  type="dashed"
                  shape="circle"
                  size="small"
                  onClick={this.handleAdd}
                  style={{
                    marginLeft: 5,
                    marginBottom: 5
                  }}
                >
                  <PlusOutlined />
                </Button>
              </Typography.Title>

              <Collapse>
                {this.renderTranslation(languages)}
              </Collapse>
            </>
          )
        }}
      </LanguageConsumer>
    )
  }
}
